/**
 * Billing API Service - Plans, Account, Payouts, Transactions
 */

import { apiClient } from './client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Transaction {
  id: number;
  date: string;
  return_number: string;
  type: 'return_fee' | 'label_credit' | 'stripe_fee' | 'platform_fee' | 'label_purchase' | 'processing_fee' | 'portal_fee' | 'subscription_fee' | 'payout' | 'correction' | 'overage_fee' | 'subscription_return_fee' | 'invoice';
  entry_type?: string;
  amount_cents: number;
  description: string;
  reference_id?: string;
}

export interface TransactionsResponse {
  transactions: Transaction[];
  total: number;
  page: number;
  per_page: number;
  balance_cents: number;
  available_periods?: string[];
  billing_period?: string | null;
}

export interface BillingTier {
  id: number;
  returns_included: number;
  price_per_return_cents: number;
  total_price_cents: number;
  is_flat?: boolean;
}

export interface BillingPlan {
  id: number;
  slug: string;
  display_name: string;
  portal_fee_cents: number;
  overage_fee_cents: number;
  tiers: BillingTier[];
}

export interface BillingAccount {
  id: number;
  customer_id: number;
  plan_id: number;
  tier_id: number | null;
  status: string;
  current_period_start: string;
  current_period_end: string;
  trial_started_at: string | null;
  trial_ends_at: string | null;
  free_returns_remaining: number;
  stripe_subscription_id: string | null;
  stripe_portal_subscription_id: string | null;
  billing_plans: {
    slug: string;
    display_name: string;
    portal_fee_cents: number;
    overage_fee_cents: number;
  };
  billing_tiers: {
    returns_included: number;
    price_per_return_cents: number;
  } | null;
}

export interface BillingOverview {
  plan_slug: string;
  plan_name: string;
  portal_fee_cents: number;
  portal_count: number;
  balance_cents: number;
  returns_this_month: number;
  label_revenue_this_month_cents: number;
  costs_this_month_cents: number;
  quota_used: number | null;
  quota_included: number | null;
  has_price_override?: boolean;
  override_portal_fee_cents?: number | null;
  override_overage_fee_cents?: number | null;
  plan_overage_fee_cents?: number | null;
  effective_overage_fee_cents?: number | null;
}

export interface Payout {
  id: number;
  amount_cents: number;
  status: 'requested' | 'processing' | 'completed' | 'rejected';
  requested_at: string;
  processed_at: string | null;
  notes: string | null;
}

export interface BillingStatement {
  id: number;
  billing_period: string;
  portal_count: number;
  portal_fees_cents: number;
  return_count: number;
  return_fees_cents: number;
  label_revenue_cents: number;
  subscription_fee_cents: number;
  total_charges_cents: number;
  total_credits_cents: number;
  net_amount_cents: number;
  status: string;
  finalized_at: string | null;
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export const billingApi = {
  // Transactions (existing)
  getTransactions: async (page = 1, perPage = 50, entryTypes?: string[], billingPeriod?: string): Promise<TransactionsResponse> => {
    const params: Record<string, string | number> = { page, per_page: perPage };
    if (entryTypes?.length) params.entry_types = entryTypes.join(',');
    if (billingPeriod) params.billing_period = billingPeriod;
    const response = await apiClient.get<TransactionsResponse>(
      '/billing/transactions',
      { params }
    );
    return response.data;
  },

  // Trial
  startTrial: async (): Promise<BillingAccount> => {
    const response = await apiClient.post<{ account: BillingAccount }>('/billing/start-trial');
    return response.data.account;
  },

  // Plans
  getPlans: async (): Promise<BillingPlan[]> => {
    const response = await apiClient.get<{ plans: BillingPlan[] }>('/billing/plans');
    return response.data.plans;
  },

  // Account
  getAccount: async (): Promise<BillingAccount | null> => {
    const response = await apiClient.get<{ account: BillingAccount | null }>('/billing/account');
    return response.data.account;
  },

  updateAccount: async (planSlug: string, tierId?: number): Promise<BillingAccount> => {
    const response = await apiClient.put<{ account: BillingAccount }>('/billing/account', {
      plan_slug: planSlug,
      tier_id: tierId,
    });
    return response.data.account;
  },

  // Overview
  getOverview: async (): Promise<BillingOverview> => {
    const response = await apiClient.get<BillingOverview>('/billing/overview');
    return response.data;
  },

  // Subscription - creates Stripe Checkout Session for recurring billing
  subscriptionCheckout: async (tierId: number): Promise<{ checkout_url: string; session_id: string }> => {
    const response = await apiClient.post<{ checkout_url: string; session_id: string }>('/billing/subscription/checkout', {
      tier_id: tierId,
    });
    return response.data;
  },

  // Payouts
  requestPayout: async (): Promise<Payout> => {
    const response = await apiClient.post<{ payout: Payout }>('/billing/payout');
    return response.data.payout;
  },

  getPayouts: async (): Promise<Payout[]> => {
    const response = await apiClient.get<{ payouts: Payout[] }>('/billing/payouts');
    return response.data.payouts;
  },

  // Statements
  getStatements: async (): Promise<BillingStatement[]> => {
    const response = await apiClient.get<{ statements: BillingStatement[] }>('/billing/statements');
    return response.data.statements;
  },

  // Combined page data (single request for SubscriptionPage)
  getPageData: async (): Promise<BillingPageData> => {
    const response = await apiClient.get<BillingPageData>('/billing/page-data');
    return response.data;
  },

  // Bank details (SEPA)
  getBankDetails: async (): Promise<BankDetails> => {
    const response = await apiClient.get<BankDetails>('/billing/bank-details');
    return response.data;
  },

  updateBankDetails: async (data: BankDetails): Promise<void> => {
    await apiClient.put('/billing/bank-details', data);
  },

  activatePlan: async (planSlug: string, portalCount: number, tierId?: number, domainCount?: number): Promise<{ checkout_url: string | null; session_id?: string; status?: string }> => {
    const response = await apiClient.post<{ checkout_url: string | null; session_id?: string; status?: string }>('/billing/activate', {
      plan_slug: planSlug,
      portal_count: portalCount,
      tier_id: tierId,
      domain_count: domainCount ?? 0,
    });
    return response.data;
  },

  confirmActivation: async (sessionId: string): Promise<{ status: string }> => {
    const response = await apiClient.post<{ status: string }>('/billing/activate/confirm', {
      session_id: sessionId,
    });
    return response.data;
  },

  updatePortalCount: async (portalCount: number): Promise<void> => {
    await apiClient.put('/billing/portal-count', { portal_count: portalCount });
  },

  updateDomainCount: async (domainCount: number): Promise<void> => {
    await apiClient.put('/billing/domain-count', { domain_count: domainCount });
  },

  openCustomerPortal: async (): Promise<{ portal_url: string }> => {
    const response = await apiClient.post<{ portal_url: string }>('/billing/customer-portal');
    return response.data;
  },

  getProrationPreview: async (newPortalCount: number): Promise<ProrationPreview> => {
    const response = await apiClient.get<ProrationPreview>(
      '/billing/proration-preview',
      { params: { new_portal_count: newPortalCount } }
    );
    return response.data;
  },
};

export interface BankDetails {
  iban: string;
  bic: string;
  account_holder: string;
}

export interface ProrationPreview {
  proration_amount_cents: number;
  next_billing_date: number | null;
  total_upcoming_cents: number;
}

// Combined response for the subscription page (1 request instead of 4)
export interface BillingPageData {
  plans: BillingPlan[];
  account: BillingAccount | null;
  overview: BillingOverview;
  payouts: Payout[];
  stripe_balance_cents: number | null;
  booked_portal_count: number;
  active_portal_count: number;
  booked_domain_count?: number;
  pending_tier_id?: number | null;
}
